import React, { useState, useEffect } from "react";
import { Form, Button, Spinner, Container, Row, Col, Card, Modal } from "react-bootstrap";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import "./index.css";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import Env from "../../hooks/Env";

const API_URL = Env().API_URL;

const UpdateCames = () => {
  useDocumentTitle('Mise à jour dossier CAMES');
  const { uid } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [options, setOptions] = useState({
    etablissements: [],
    grades_actuels_cames: [],
    grades_postules_cames: [],
    cts_cames: [],
  });

  const [formData, setFormData] = useState({
    prenom: "",
    nom: "",
    email: "",
    etablissement: "",
    grade_actuel_cames: "",
    grade_postule_cames: "",
    cts_cames: "",
    annee_depot: new Date().getFullYear(),
  });

  // Fetch options for select fields
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await axios.get(`${API_URL}/cames/types-reponses`);
        setOptions(response.data);
      } catch (err) {
        console.error("Erreur lors du chargement des options:", err);
        setError("Impossible de charger les options du formulaire");
      }
    };
    fetchOptions();
  }, []);

  // Fetch candidate data
  useEffect(() => {
    const fetchCandidate = async () => {
      try {
        const response = await axios.get(`${API_URL}/cames/uid/${uid}`);
        const data = response.data;
        setFormData({
          prenom: data.prenom || "",
          nom: data.nom || "",
          email: data.email || "",
          etablissement: data.etablissement || "",
          grade_actuel_cames: data.grade_actuel_cames || "",
          grade_postule_cames: data.grade_postule_cames || "",
          cts_cames: data.cts_cames || "",
          annee_depot: data.annee_depot || new Date().getFullYear(),
        });
        setLoadingData(false);
      } catch (err) {
        console.error("Erreur lors du chargement du candidat:", err);
        if (err.response?.status === 404) {
          setNotFound(true);
        } else {
          setError("Impossible de charger les informations du candidat");
        }
        setLoadingData(false);
      }
    };
    if (uid) {
      fetchCandidate();
    }
  }, [uid]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const response = await axios.put(`${API_URL}/cames/${uid}`, formData, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      console.log("Réponse:", response.data);
      setSuccess(true);
    } catch (err) {
      console.error("Erreur lors de la mise à jour:", err);
      setError(err.response?.data?.detail || "Une erreur est survenue lors de la mise à jour");
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <Container className="cames-container d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (notFound) {
    return (
      <Container className="cames-container py-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="cames-card shadow">
              <Card.Body className="text-center py-5">
                <div className="mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" fill="#dc3545" viewBox="0 0 16 16">
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                    <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/>
                  </svg>
                </div>
                <h4 className="mb-3">Candidat non trouvé</h4>
                <p className="text-muted">
                  Aucun candidat ne correspond à cet identifiant.
                </p>
                <Button variant="primary" onClick={() => navigate('/cames')}>
                  Retour au formulaire
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="cames-container py-5">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="cames-card shadow">
            <Card.Header className="cames-header text-center">
              <h3 className="mb-1">Programme CAMES VISION 360</h3>
              <p className="mb-0">Mise à jour des informations du candidat</p>
            </Card.Header>
            <Card.Body className="p-4">
              {/* Modal de succès */}
              <Modal show={success} onHide={() => navigate('/')} centered>
                <Modal.Header closeButton className="bg-success text-white">
                  <Modal.Title>
                    <i className="bi bi-check-circle-fill me-2"></i>
                    Mise à jour réussie
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center py-4">
                  <div className="mb-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" fill="#198754" viewBox="0 0 16 16">
                      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                    </svg>
                  </div>
                  <h5 className="mb-3">Vos informations ont été mises à jour avec succès !</h5>
                  <p className="text-muted mb-0">
                    Les modifications ont été enregistrées.
                  </p>
                </Modal.Body>
                <Modal.Footer className="justify-content-center">
                  <Button variant="success" onClick={() => navigate('/')}>
                    Fermer
                  </Button>
                </Modal.Footer>
              </Modal>

              {/* Modal d'erreur */}
              <Modal show={!!error} onHide={() => setError("")} centered>
                <Modal.Header closeButton className="bg-danger text-white">
                  <Modal.Title>
                    Erreur
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center py-4">
                  <div className="mb-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" fill="#dc3545" viewBox="0 0 16 16">
                      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z"/>
                    </svg>
                  </div>
                  <h5 className="mb-3">Une erreur est survenue</h5>
                  <p className="text-muted mb-0">
                    {error}
                  </p>
                </Modal.Body>
                <Modal.Footer className="justify-content-center">
                  <Button variant="danger" onClick={() => setError("")}>
                    Fermer
                  </Button>
                </Modal.Footer>
              </Modal>

              <Form onSubmit={handleSubmit}>
                {/* Prénom */}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">
                    Prénom <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                    placeholder="Entrez votre prénom"
                    required
                  />
                </Form.Group>

                {/* Nom */}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">
                    Nom <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    placeholder="Entrez votre nom"
                    required
                  />
                </Form.Group>

                {/* Email */}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">
                    Email <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Entrez votre adresse email"
                    required
                  />
                </Form.Group>

                {/* Etablissement */}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">
                    Établissement <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="etablissement"
                    value={formData.etablissement}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Sélectionnez un établissement --</option>
                    {options.etablissements.map((etab, index) => (
                      <option key={index} value={etab}>
                        {etab}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                {/* Grade actuel CAMES */}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">
                    Grade actuel CAMES <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="grade_actuel_cames"
                    value={formData.grade_actuel_cames}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Sélectionnez votre grade actuel --</option>
                    {options.grades_actuels_cames.map((grade, index) => (
                      <option key={index} value={grade}>
                        {grade}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                {/* Grade postulé CAMES */}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">
                    Grade postulé CAMES <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="grade_postule_cames"
                    value={formData.grade_postule_cames}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Sélectionnez le grade postulé --</option>
                    {options.grades_postules_cames.map((grade, index) => (
                      <option key={index} value={grade}>
                        {grade}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                {/* CTS CAMES */}
                <Form.Group className="mb-4">
                  <Form.Label className="fw-medium">
                    CTS CAMES <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="cts_cames"
                    value={formData.cts_cames}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Sélectionnez le CTS --</option>
                    {options.cts_cames.map((cts, index) => (
                      <option key={index} value={cts}>
                        {cts}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                {/* Submit Button */}
                <div className="d-grid">
                  <Button
                    variant="primary"
                    type="submit"
                    size="lg"
                    disabled={loading}
                    className="cames-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Mise à jour en cours...
                      </>
                    ) : (
                      "Mettre à jour mes informations"
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default UpdateCames;
